import h from 'hyperscript';
// be sure to load the base table before the plugins
import settingsIcon from '@disy/cadenza-icons/settings.svg';
import reportIcon from '@disy/cadenza-icons/report.svg';
import mapIcon from '@disy/cadenza-icons/map.svg';
import chartIcon from '@disy/cadenza-icons/chart.svg';
import tableIcon from '@disy/cadenza-icons/table.svg';
import isEqual from 'lodash-es/isEqual';

import { BEFORE_LOAD_DATA, ERROR_LOAD_DATA, LOAD_DATA, LOAD_DATA_CANCELED } from 'ui/data-loading-event-types';
import type { AlertMessage, Alerts } from 'ui/alerts/alerts';
import type { Data } from 'ui/data/data';
import type { DataTablesRequestParams } from 'ui/datatables/datatables';

import type {
  BaseTableAjaxFunction,
  BaseTableAjaxRequestParams,
  BaseTableDataLoadCanceledEventDetails,
  BaseTableOptions
} from 'cadenza/ui/base-table/base-table';
import {
  BaseTable,
  COLUMN_WITH_VERTICAL_SORTING_BUTTON_IN_HEADER_CLASS,
  createHeaderCellContents,
  getColumnIndexForBackend,
  getColumnIndexForFrontend
} from 'cadenza/ui/base-table/base-table';
import { SortOrder } from 'cadenza/ui/base-table/sort-order';
import { encodeUriAndSingleQuote } from 'cadenza/ui/base-table/table-data-renderer';
import type { PreparedOlapPivotTableColumn } from 'cadenza/result-table/pivot-table-features/pivot-table-utils';
import { icon } from 'cadenza/utils/icon/icon';
import { escapeHTML } from 'cadenza/utils/escape-html';
import { getUrlParam, setParam } from 'cadenza/utils/url-param-utils';
import { AbortError } from 'cadenza/api-client/abort-controller/abort-error';
import {
  displayOnDemandViewWithStateCanceled,
  getOnDemandViewElement,
  setOnDemandViewStateForFinishedLoading
} from 'cadenza/on-demand-view/on-demand-view';
import { MultiExecutionHelper } from 'cadenza/api-client/abort-controller/multi-execution-helper';
import { BackendAbortController } from 'cadenza/api-client/abort-controller/backend-abort-controller';
import { EXECUTION_CONFIRMED, PARAMETER_CONDITION_HASH } from 'cadenza/api-client/api-constants';
import { CONDITIONS_HIERARCHIES_CHANGE } from 'cadenza/conditions/event-types';
import { ResultTablePlugin } from 'cadenza/result-table/result-table-plugin';
import { logger } from 'cadenza/utils/logging';
import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { createTableExportMenuButton } from 'cadenza/actions/table/create-table-export-menu-actions';
import { executeParallelViewsProcess, isCustomTemplatePostMessageProcessing } from 'cadenza/processes/processes-list';
import { getProcessingUrl, getTableConfiguration, getTableData } from 'cadenza/api-client/processing-chain-api';
import { showProcessDialog } from 'cadenza/processes/process-dialog';
import { getSubnav } from 'cadenza/utils/classic-dependency-registry';
import { hashCode } from 'cadenza/utils/hash-code';
import { isSafari } from 'cadenza/utils/browser';
import { isFeatureAvailable } from 'cadenza/features';
import type { ResponseProblem } from 'cadenza/api-client/api';
import { frontendSingleExecution } from 'cadenza/api-client/api';
import { legacySetupScrollShadow } from 'cadenza/utils/scroll-shadow/scroll-shadow';
import { doesUserTryToOpenInNewTabOrNewWindow } from 'cadenza/utils/input-events';
import { SelectionResultTablePlugin } from 'cadenza/result-table/selection/selection-result-table-plugin';
import { INDEX_COLUMN_INDEX } from 'cadenza/result-table/result-table-index-column-index';
import { classicPostMessage } from 'cadenza/integration/classic-post-message';
import type { TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';
import type { SelectorResultCount } from 'cadenza/selector-result-count/selector-result-count';
import type { ConditionChangeEventDetails, FilterController } from 'cadenza/conditions/filter-controller';
import type { TableController } from 'cadenza/result-table/table-controller';
import type {
  ChainingProcessingDto,
  DefaultProcessingDto,
  TableColumnDto,
  TemplateProcessingDto,
  WebApplicationProcessingDto
} from 'cadenza/ui/base-table/model/table-column-dto';
import type { ColumnIdentificationDto } from 'cadenza/workbook/selection/model/column-origin-dto';
import type { TableConfigurationWithOriginDto } from 'cadenza/workbook/tables/workbook-table-view';
import type { ColumnAttributeGroupDto } from 'cadenza/result-table/column-attribute-group-dto';
import type { PreparedResultTableColumn } from 'cadenza/result-table/prepared-result-table-column';
import type { TableDataDto } from 'cadenza/ui/base-table/model/table-data-dto';
import type { TableRowDto } from 'cadenza/ui/base-table/model/table-row-dto';
import { assert } from 'cadenza/utils/custom-error';
import { isProblem } from 'cadenza/utils/problem-util';
import {
  getDataTableElement,
  getHeaderRowHasSubTitle,
  parseCommaSeparateAttribute,
  setDataTableMarginTop
} from 'cadenza/result-table/result-table-utils';
import { DISABLE_RESIZING_COLUMN_CLASS_NAME } from 'cadenza/ui/base-table/resize-column-base-table-plugin';
import {
  LobDatatypeIconResultTablePlugin
} from 'cadenza/result-table/lob-datatype-icon/lob-datatype-icon-result-table-plugin';

import type { AjaxResponse, ConfigColumnDefs, DataTableState, FunctionColumnRender } from 'datatables.net';

import './result-table.css';
import i18n from './messages.properties';

export const RESULT_TABLE_OPTION_ORDERBYCOLUMN_NAME = 'orderByColumnName';
const RESULT_TABLE_OPTION_ORDERBYCOLUMN = 'orderByColumn';
export const RESULT_TABLE_OPTION_ORDERASC = 'orderAsc';
const ON_DEMAND_EXECUTION = 'on-demand-execution';
const COMPONENT_NAME = 'd-table';

const COLUMN_DEFAULTS = {
  alignment: 'LEFT',
  ordering: 'UNSORTED'
} as const satisfies Partial<PreparedResultTableColumn>;

const PROCESSING_ICONS: Record<string, string> = {
  table: tableIcon,
  resulttable: tableIcon,
  selector: tableIcon,
  conditionResult: tableIcon,

  chart: chartIcon,
  overlaychart: chartIcon,

  map: mapIcon,
  spatialvis: mapIcon,

  report: reportIcon,
  script: reportIcon,

  custom: settingsIcon
};

// Buffer size needs to be higher than the number of fetches that are triggered by scrolling most
// likely there will be 2-3 fetches triggered by scrolling, but 200 is way higher so we are safe.
const DEFAULT_MULTI_EXECUTION_HELPER_BUFFER_SIZE = 200;

export type ResultTableOptions = BaseTableOptions & {
  tableConfiguration?: TableConfigurationDto;
  tableConfigurationProvider?: (abortSignal: AbortSignal) => Promise<TableConfigurationDto>;
  multiExecutionHelperBufferSize?: number;
  allowHeaderRowSubtitle?: boolean;
  ajax?: ResultTableAjaxFunction;
};

export type ResultTableAjaxRequestParams = BaseTableAjaxRequestParams & {
  signal?: AbortSignal;
};
export type ResultTableAjaxFunction = (requestParams: ResultTableAjaxRequestParams) => Promise<TableDataDto>;

type ResultTableAjaxResponse = AjaxResponse & {
  uniqueRecordsTotal: number;
};

export type AbortReason = 'CONDITION_CHANGE' | 'MANUAL';

export interface ResultTableHierarchicalColumns {
  multiColumns: PreparedResultTableColumn[][];
  rowColumns: PreparedResultTableColumn[];
}

export interface PreparedResultTableColumns {
  columns: PreparedResultTableColumn[];
  hierarchicalColumns: ResultTableHierarchicalColumns;
}

type ResultTableProcessing = TemplateProcessingDto & {
  sourceProcessingRow: number;
  sourceOrderByColumn?: number;
  sourceOrderAsc?: boolean;
  url: string;
};

type ResultTableRow = TableRowDto & {
  index: number;
};

export class ResultTable extends BaseTable {

  #lastConditionValuesSetHash: string | null = null;
  #loadTableDataExecutionHelper;
  #abortReason?: AbortReason;
  #tableConfiguration;
  #tableConfigurationProvider;
  #isInvalidTableConfiguration = false;
  #hideWhenTruncatedData = false;
  #allowHeaderRowSubtitle;
  #resizeObserver;
  #footerRequiresTableWidthRecalculationDueToNewData = false;
  #eventKeys?: EventKey[];
  protected alerts!: Alerts;
  #invalidTableConfigurationMessage!: HTMLElement;
  protected tableController?: TableController;
  #excelDownloadButton?: HTMLButtonElement;
  #configurationColumns?: TableColumnDto[];
  #columnIdentifications?: Record<string, ColumnIdentificationDto>;
  #customConditionResultTableAttributeIndices?: object[];
  #columnAttributeGroups?: ColumnAttributeGroupDto[];
  #preparedColumns?: PreparedResultTableColumns;
  #aggregationFooters?: string[];

  constructor ({
    tableConfiguration,
    tableConfigurationProvider,
    multiExecutionHelperBufferSize = DEFAULT_MULTI_EXECUTION_HELPER_BUFFER_SIZE,
    allowHeaderRowSubtitle = true,
    ...baseTableOptions
  }: ResultTableOptions = {}) {
    if (!baseTableOptions.ajax) {
      baseTableOptions.ajax = getTableData;
    }
    if (!baseTableOptions.stateSaveCallback) {
      baseTableOptions.stateSaveCallback = defaultStateSaveCallback;
    }
    if (!baseTableOptions.stateLoadCallback) {
      baseTableOptions.stateLoadCallback = defaultStateLoadCallback;
    }
    super(baseTableOptions);

    this.#allowHeaderRowSubtitle = allowHeaderRowSubtitle;
    this.#tableConfiguration = tableConfiguration; // Initial settings
    this.#tableConfigurationProvider = frontendSingleExecution(
      tableConfigurationProvider
      ?? ((signal) => getTableConfiguration({
        ...this.#prepareReadConfigRequestParams(),
        signal
      })));
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.dataTable) {
        this.resizeTableScrollHeight();
      }
    });

    this.#loadTableDataExecutionHelper = new MultiExecutionHelper(multiExecutionHelperBufferSize);

    // Default plugins
    this.append(new LobDatatypeIconResultTablePlugin());
    if (!baseTableOptions.renderForPrint) { // Not necessary for reports
      this.append(new SelectionResultTablePlugin());
    }
  }

  // Browsing context in which the table is displayed (embedded-content for tables in workbooks and body for classic tables)
  get #enclosingContext () {
    return this.closest('.d-embedded-content') ?? document.body;
  }

  get allowHeaderRowSubtitle () {
    return this.#allowHeaderRowSubtitle;
  }

  set allowHeaderRowSubtitle (value) {
    this.#allowHeaderRowSubtitle = value;
  }

  override connectedCallback () {
    this.classList.add(COMPONENT_NAME);

    this.initTableController();
    this.#initAlerts();
    this.#resizeObserver.observe(this.#enclosingContext);

    this.#eventKeys = [];
    // TODO_LATER (ks/jki/bte, 2019-09-18, TPM-196, CADENZA-11668/CADENZA-11674): move this wiring logic outside of the component, maybe in the entry
    const eventSource = document.querySelector<FilterController>('.d-filter-controller')
      || this.closest('.on-demand-parent')?.querySelector('.d-on-demand-view');
    if (eventSource) {
      this.#eventKeys.push(
        on<CustomEvent<ConditionChangeEventDetails>>(eventSource, CONDITIONS_HIERARCHIES_CHANGE, event => this.#onConditionsOrHierarchiesChange(event))
      );
    }
    // TODO_LATER (ks/jki/bte, 2019-09-18, TPM-196, CADENZA-11668/CADENZA-11674): move this wiring logic outside of the component, to the entry for the page
    const selectorResultCount = document.querySelector<SelectorResultCount>('.d-selector-result-count');
    if (selectorResultCount) {
      this.#eventKeys.push(
        on<CustomEvent<ResultTableAjaxResponse>>(this, LOAD_DATA, (event) => selectorResultCount.setValues(event.detail.recordsTotal, event.detail.uniqueRecordsTotal)),
        on(this, BEFORE_LOAD_DATA, () => selectorResultCount.setLoading()),
        on(this, LOAD_DATA_CANCELED, () => selectorResultCount.hideLoading()),
        on(this, ERROR_LOAD_DATA, () => selectorResultCount.hideLoading()));
    }
    this.#eventKeys.push(on<CustomEvent<BaseTableDataLoadCanceledEventDetails>>(this, LOAD_DATA_CANCELED, (event) => this.#onLoadDataCanceled(event.detail)));
    this.#eventKeys.push(on(this.#enclosingContext, 'scroll', () => {
      if (this.dataTable) {
        this.resizeTableScrollHeight();
      }
    }));
    this.alerts = this.closest('.table-results-table')!.querySelector<Alerts>('.d-alerts')!;
    // TODO_LATER (ks/jki/bte, 2019-09-18, TPM-196, CADENZA-11668/CADENZA-11674): this should optimally be moved to the page-level-wiring, the entry?
    this.#addDownloadButtonToSubnav();

    super.connectedCallback();

    const tableConfiguration = this.#tableConfiguration ?? (this.previousElementSibling as Data<TableConfigurationDto>).value;
    if (tableConfiguration && Object.keys(tableConfiguration).length !== 0) {
      this.tableConfiguration = tableConfiguration;
    } else { // We need to fetch it using the TableConfigurationProvider function
      this.updateTableConfiguration();
    }
    this.registerPostMessageListener();
  }

  protected initTableController () {
    this.tableController = document.querySelector('.d-table-controller') ?? undefined;
    if (this.tableController) {
      this.tableController.initResultTable(this);
    }
  }

  #initAlerts () {
    this.#invalidTableConfigurationMessage = h('.dimmed-backdrop.table-configuration',
      { hidden: true },
      h('span.headline', i18n('invalidTableConfiguration')));
    this.parentNode!.append(this.#invalidTableConfigurationMessage);
  }

  registerPostMessageListener () {
    if (isFeatureAvailable('WEBAPP_INTEGRATION')) {
      classicPostMessage.registerEventListener(() => this.handleTableRefresh(), [ 'net.disy.cadenza.table.command.reloadTable' ]);
    }
  }

  handleTableRefresh () {
    setParam({ paramName: 'forceRefresh', paramValue: true });
    this.refresh();
  }

  override disconnectedCallback () {
    super.disconnectedCallback();
    // TODO_LATER (ks/jki/bte, 2019-09-18, TPM-196, CADENZA-11668/CADENZA-11674): see download button removal higher up
    if (this.#excelDownloadButton) {
      this.#excelDownloadButton.remove();
    }
    this.#resizeObserver.disconnect();
    unByKey(this.#eventKeys);
  }

  override getDataTableOptions (columns: TableColumnDto[], ajax?: BaseTableAjaxFunction) {
    const tableOptions = super.getDataTableOptions(columns, ajax);
    const dataTableOptions = tableOptions.dataTableOptions;
    dataTableOptions.stateSave = true;

    // Assigning unique row id from TableColumnDto.id to each row. This allows selection of
    // datatable rows via dataTable.row('#uniqueRowId') and also returning id via row().id() method.
    dataTableOptions.rowId = 'id';

    return tableOptions;
  }

  hasPivotConfiguration () {
    return false;
  }

  abort (abortReason: AbortReason, oldConditionValuesSetHash?: string) {
    if (!oldConditionValuesSetHash || oldConditionValuesSetHash === this.#lastConditionValuesSetHash) {
      this.#abortReason = abortReason;
      this.cancelRequests();
    }
  }

  override onPreInit (settings: object) {
    this.resizeTableScrollHeight(settings);
  }

  get configurationColumns () {
    assert(this.#configurationColumns, 'Table configuration not available, yet');
    return [ ...this.#configurationColumns ];
  }

  /**
   * @return The column attribute groups
   */
  protected get columnAttributeGroups () {
    assert(this.#columnAttributeGroups, 'Table configuration not available, yet');
    return [ ...this.#columnAttributeGroups ];
  }

  /**
   * Column identification information used to map between table columns and query layer attributes.
   *
   * @return The column
   *   identifications from table configuration by column id for workbook tables, undefined for
   *   non-workbook olap tables.
   */
  get columnIdentifications () {
    return this.#columnIdentifications;
  }

  get styleClass () {
    return this.getAttribute('styleClass');
  }

  // TODO_LATER (ks/jki/bte, 2019-09-18, TPM-196, CADENZA-11668/CADENZA-11674): custom filtering? Should be in plugin?
  get customConditionResultTableAttributeIndices () {
    return this.#customConditionResultTableAttributeIndices;
  }

  override refresh () {
    // in case the columns are not initialized
    // use case is workflows with ttd and mandatory condition - in this case, the columns are not initialized
    // see net.disy.cadenza.table.web.TableColumnConfiguration.EMPTY
    if (this.getColumns().length === 0) {
      this.updateTableConfiguration();
    } else {
      super.refresh();
    }
  }

  // TODO_LATER (ks/jki/bte, 2019-09-18, TPM-196, CADENZA-11668/CADENZA-11674): can we replace these update calls with
  // different logic, depending on table type with a more generic refresh() call that the outside world calls and the
  // implementation decides what to do
  updateTableConfiguration () {
    this.cancelRequests(); // Cancel all currently running data or configuration requests
    this.destroyDataTable();
    return this.fetchConfigurationAndInitializeDataTable();
  }

  /**
   * Cancel running requests.
   */
  protected cancelRequests () {
    this.#loadTableDataExecutionHelper.cancel(); // Cancel all currently running data or configuration requests
  }

  /**
   * Fetches the current table configuration using the configured provider and update the table to
   * the result.
   *
   * @return Promise that resolves when the table has been updated
   */
  protected fetchConfigurationAndInitializeDataTable () {
    return this.fetchConfiguration()
      .then(columnConfiguration => {
        this.tableConfiguration = columnConfiguration;
      })
      .catch(error => {
        if (error instanceof AbortError) {
          // do nothing as the only way this gets aborted is, when fetchConfigurationAndInitializeDataTable has been called again
        } else {
          logger.error('Failed to load table configuration', error);
        }
      });
  }

  /**
   * Fetches the current table configuration using the configured provider.
   *
   * @return Promise returning the new table configuration when ready
   */
  protected fetchConfiguration (): Promise<TableConfigurationDto> {
    // For loading the table config (1st request), we don't show a cancel button so that
    // we only handle the cancel/reload lifecycle for the 2nd request (table data).
    // See: https://jira.disy.net/browse/CADENZA-15662

    // If shouldLoadData() is false then d-on-demand-view is visible and progress spinner
    // should not be shown.
    const shouldLoadData = this.shouldLoadData();
    if (shouldLoadData) {
      this.showLoadingStateAndSpinner();
    }
    this.#lastConditionValuesSetHash = getUrlParam({ name: PARAMETER_CONDITION_HASH });
    const abortController = new AbortController();
    this.#loadTableDataExecutionHelper.submit(abortController);
    return this.#tableConfigurationProvider(abortController.signal)
      .catch(error => {
        // When fetching configuration, queries can be executed, and if the query failed: internal error is thrown by backend.
        // This logic makes sure that the user is informed of that error.
        this.handleLoadDataError(error);
        this.hideLoadingStateAndSpinner();
      })
      .finally(() => {
        if (shouldLoadData) {
          // Hide the spinner to indicate that the configuration is fetched, but do not alter the view
          // loading state. The LOAD_END event is fired once the table data is loaded.
          this.hideProgressSpinner();
        } else {
          this.hideLoadingStateAndSpinner();
        }
      });
  }

  /**
   * Refresh the table data only. This will fire an AJAX-request if the table
   * was configured with an ajax property.
   */
  updateTableData () {
    // Due to timing issues with live mode and long-running requests, it is
    // possible to arrive here without the data table being in place.
    const dataTable = this.getDataTable();
    if (dataTable) {
      return new Promise<void>((resolve) => {
        const callback = () => {
          dataTable.off('draw', callback);
          resolve();
        };
        dataTable.on('draw', callback);
        this.getDataTable()?.draw();
      });
    }
    return Promise.resolve();
  }

  get tableConfiguration () {
    return this.#tableConfiguration!;
  }

  /**
   * Initializes the table with the new table configuration
   *
   * @param tableConfiguration - The new table configuration
   */
  set tableConfiguration (tableConfiguration: TableConfigurationDto) {
    // Update Configuration
    this.configureWithConfigurationData(tableConfiguration);

    // Update UI
    if (!this.isInvalidTableConfiguration) {
      this.destroyDataTable();
      this.initializeDataTable();
    }

    this.updateMessages();
  }

  protected configureWithConfigurationData (tableConfiguration: TableConfigurationDto) {
    // Reset because obsolete. Will be set after successful table body data requests. Also used to
    // determine changes between footers. Without resetting here we cannot differ footer
    // changes caused by new and same configuration.
    this.#aggregationFooters = undefined;

    this.#tableConfiguration = tableConfiguration;
    this.#configurationColumns = tableConfiguration.configurationColumns;
    this.#columnAttributeGroups = tableConfiguration.columnAttributeGroups;
    this.persistenceId = tableConfiguration.persistenceId;
    this.#customConditionResultTableAttributeIndices = tableConfiguration.customConditionResultTableAttributeIndices;
    this.#columnIdentifications = (tableConfiguration as TableConfigurationWithOriginDto).columnIdentifications;

    this.#preparedColumns = this.prepareColumns(tableConfiguration.columns);

    for (const plugin of this.getPlugins(ResultTablePlugin)) {
      plugin.configureWithConfigurationData(tableConfiguration);
    }
  }

  override getColumns () {
    return this.#preparedColumns?.columns ?? [];
  }

  getHierarchicalColumns () {
    return this.#preparedColumns?.hierarchicalColumns;
  }

  #prepareReadConfigRequestParams () {
    const param = new URLSearchParams(window.location.search);
    return {
      orderByColumn: param.get(RESULT_TABLE_OPTION_ORDERBYCOLUMN),
      orderAsc: param.get(RESULT_TABLE_OPTION_ORDERASC)
    };
  }

  override shouldLoadData () {
    if (this.#isOnDemandExecution()) {
      if (this.#wasExecutionConfirmed()) {
        const onDemandView = getOnDemandViewElement();
        if (onDemandView && !onDemandView.hidden) {
          return false;
        }
      } else {
        return false;
      }
    }

    return super.shouldLoadData(); // Only if base class agrees to render
  }

  override onFirstTableDraw () {
    this.#updateFooter();

    super.onFirstTableDraw();

    const scrollBody = this.getScrollBody();
    if (!scrollBody) { // Scroller disabled in report mode (renderForPrint = true)
      return;
    }

    this.resizeTableScrollHeight();

    if (!isSafari) {
      const dataTableElement = getDataTableElement(scrollBody);
      const scrollFoot = this.getScrollFoot();
      const scrollHead = this.getScrollHead()!;

      scrollBody.prepend(scrollHead);
      scrollHead.classList.add('ResultTable--sticky-header');

      legacySetupScrollShadow(scrollBody, scrollHead, scrollFoot);

      // Some code which we couldn't find does change the height of the pivot column headers later on.
      // To include that change we update the margin in the next loop.
      setTimeout(() => setDataTableMarginTop(scrollHead, dataTableElement));

      if (scrollFoot) {
        scrollBody.append(scrollFoot);
        scrollFoot.classList.add('ResultTable--sticky-footer');
      }
    }
  }

  #updateFooter () {
    // This has to be executed even if this.hasFooter() returns false, because on a technical level
    // there is always a footer. Datatables need it to calculate the column widths.
    this.#correctFooterPosition();
    const hasFooter = this.hasFooter();
    if (hasFooter) {
      this.#fillFooter(this.getColumns());
    }
    this.classList.toggle('with-footer', hasFooter);
  }

  override beforeLoadedData () {
    this.dispatchEvent(new CustomEvent(BEFORE_LOAD_DATA));
  }

  override prepareRequestParams (requestParams: DataTablesRequestParams) {
    // keep last condition values set hash and abort controller in sync
    this.#lastConditionValuesSetHash = getUrlParam({ name: PARAMETER_CONDITION_HASH });
    const abortController = new BackendAbortController();
    abortController.abortOnUnload = true;
    const cancelButton = document.getElementById('cancel-result-table-load-button')
      || this.querySelector('.full-content-spinner.d-progress-spinner-overlay button');
    if (cancelButton) {
      cancelButton.onclick = () => {
        this.cancelDataLoading();
      };
    }
    this.#loadTableDataExecutionHelper.submit(abortController);
    this.#abortReason = 'CONDITION_CHANGE';
    const onDemandView = getOnDemandViewElement();
    if (onDemandView) {
      onDemandView.hidden = true;
    }

    return Object.assign(super.prepareRequestParams(requestParams), {
      signal: abortController.signal
    });
  }

  cancelDataLoading () {
    this.#abortReason = 'MANUAL';
    this.cancelRequests();
  }

  override getAbortReason () {
    return this.#abortReason;
  }

  override onLoadedData (loadedData: TableDataDto, requestParams: DataTablesRequestParams) {
    // Aggregation footers are part of the body data request, not the configuration, so in case of a
    // changed footer we need to redraw the whole table to both, make the footer visible and make it
    // affect the column width calculation. A more optimal solution would just update the footer and
    // re-trigger the column width calculation, but this is not an easy task because we would need to
    // set back the table to its initial state to result in the same measurements which is currently
    // not supported by our and datatable's (and its plugins) code.
    if (this.#aggregationFooters && !isEqual(this.#aggregationFooters, loadedData.aggregationFooters)) {
      this.#footerRequiresTableWidthRecalculationDueToNewData = true;
    }

    this.#aggregationFooters = loadedData.aggregationFooters;

    loadedData.rows.forEach((row, i) => {
      row.id = row.id || hashCode(JSON.stringify(row.values)).toString();
      (row as ResultTableRow).index = i + requestParams.start; // add pagination offset
    });

    // Row number column
    addIndexColumn(loadedData.rows, requestParams.start);

    this.#updateAlertsAndTableVisibility(loadedData.truncated !== 'NO', loadedData.rowsTotal);

    this.dataset.noResults = String(!loadedData.rowsTotal);

    return {
      data: loadedData.rows,
      recordsFiltered: loadedData.rowsTotal,
      recordsTotal: loadedData.rowsTotal,
      uniqueRecordsTotal: loadedData.uniqueRowCount
    };
  }

  override destroyDataTable () {
    super.destroyDataTable();
    this.#footerRequiresTableWidthRecalculationDueToNewData = false;
  }

  override shouldRerenderWholeTableDueToNewData () {
    if (this.#footerRequiresTableWidthRecalculationDueToNewData) {
      return true;
    }
    return super.shouldRerenderWholeTableDueToNewData();
  }

  override handleLoadDataError (error: ResponseProblem) {
    this.updateMessages();
    const alerts = this.alerts.alerts;

    const problem = isProblem(error);
    if (problem && error.key === 'result-limits-exception') {
      alerts.push({
        type: 'warning',
        message: error.detail as string
      });
      this.style.visibility = 'hidden';
    } else if (problem && error.key === 'result-empty-condition-exception') {
      alerts.push({
        type: 'info',
        message: i18n('common:noResult')
      });
      this.dataset.noResults = 'true';
      this.style.visibility = 'hidden';
    } else if (problem && error.key === 'insufficient-repository-configuration') {
      alerts.push({
        type: 'warning',
        message: error.detail as string
      });
    } else {
      alerts.push({
        type: 'error',
        message: i18n('dataAccessError')
      });
      this.style.visibility = 'hidden';

      // Print error to console to not hide programming errors and easy debugging of installations at customer
      logger.error('Error while loading data', error);
    }

    this.alerts.alerts = alerts;
    this.dispatchDataLoadError(error);
  }

  override afterLoadedData () {
    setOnDemandViewStateForFinishedLoading();
    super.afterLoadedData();
  }

  /**
   * If set to true, the result table is hidden when the table data is truncated (more results than
   * the defined limit). If false, a warning shown but the table is displayed anyway.
   *
   * @param value - true, if result table should be hidden when data is truncated; false otherwise
   */
  protected set hideWhenTruncatedData (value) {
    this.#hideWhenTruncatedData = value;
  }

  get hideWhenTruncatedData () {
    return this.#hideWhenTruncatedData;
  }

  #updateAlertsAndTableVisibility (truncated: boolean, rowsTotal: number) {
    this.updateMessages();

    const alerts: AlertMessage[] = this.alerts.alerts;
    if (truncated && this.hideWhenTruncatedData) {
      alerts.push({
        type: 'error',
        title: i18n('truncatedErrorTitle'),
        message: i18n('truncatedErrorMessage', { rows: rowsTotal })
      });
    } else if (truncated) {
      alerts.push({
        type: 'warning',
        title: i18n('truncatedWarningTitle'),
        message: i18n('truncatedWarningMessage', { rows: rowsTotal })
      });
    }
    if (rowsTotal === 0) {
      alerts.push({
        type: 'info',
        message: i18n('common:noResult')
      });
    }

    this.alerts.alerts = alerts;

    // table visibility
    const isTableHidden = rowsTotal === 0 || (this.#hideWhenTruncatedData && truncated);
    this.style.visibility = isTableHidden ? 'hidden' : 'visible';
  }

  override getColumnDefinitions (columns: TableColumnDto[], targetIndex = 0) {
    const headerRowHasSubTitle = getHeaderRowHasSubTitle(this.getColumns()) && this.#allowHeaderRowSubtitle;
    const definitions: ConfigColumnDefs[] = [];

    // Add the row number column
    definitions.push({
      defaultContent: '',
      orderable: false,
      /* target property is not important for us since define our own data function below.
         We are setting it sensibly anyway, as it is required */
      target: targetIndex,
      /* Although the index column is not resizable and the content is not primitive text-content, we have to add
         .ellipsis class like for the other columns in base implementation, because for Chrome it makes a difference in
         height calculation if or if not all cells of a row have overflow defined. We could also simply set the overflow
         property, but using the same class ensures, having the same source and values of the defined properties.
         If we don't do this, the vertical-sticky footer table would expand its container by 1px vertically, leading to
         jumping vertically when scrolling to the bottom of the table. */
      className: `ellipsis ${DISABLE_RESIZING_COLUMN_CLASS_NAME}`,
      data: (row, type, set, meta) => row.formatted[meta.col],
      visible: this.showRowNumberColumn,
      createdCell: (td, cellData, rowData, rowIndex, colIndex) =>
        this.onDatatableCreateCell(td, colIndex, cellData, null, rowData, rowIndex)
    });

    columns.forEach(column => {
      let def;
      if (column.processing) {
        def = {
          defaultContent: '',
          orderable: false,
          /* target property is not important for us since define our own data function below.
             We are setting it sensibly anyway, as it is required */
          target: targetIndex++,
          render: this.#getProcessingColumnRenderer(column),
          className: 'processing-column'
        };
      } else {
        def = super.getColumnDefinitions([ column ], targetIndex++)[0];
        def.data = (row, type, set, meta) => row.formatted[meta.col];
        def.render = super.getRenderer(column);
        def.className = `${def.className ?? ''} column-type-${column.attributeType}`.trim();

        /*
          We need to mark the columns that have the vertical sorting button to compensate for their
          width in the body cells when using right text-alignment for the column.
         */
        if (def.orderable) {
          def.className += ` column-type-${column.attributeType} ${COLUMN_WITH_VERTICAL_SORTING_BUTTON_IN_HEADER_CLASS}`;
        }

        // We are using || instead of ?? because we want to show topNInformation even
        // if headerSubTitle is set to ''.
        const subtitle = this.#allowHeaderRowSubtitle
          ? column.headerSubTitle || column.topNInformation || ''
          : '';
        const headerCellContentsOptions = {
          subTitle: subtitle,
          headerRowHasSubTitle
        };
        def.title = createHeaderCellContents(column.title, headerCellContentsOptions).outerHTML;
      }
      definitions.push(def);
    });

    // Datatable will override the header cells with the title defined to it. However, it does for
    // uniformily for all cells of a column, overriding the content that we carefully defined upfront as real cells.
    // To avoid this, the code makes an exception to that rule when autoTitle property is defined to true.
    // This does not seem to be an official feature, but still is better than patching datatables.
    for (const def of definitions) {
      (def as ConfigColumnDefs & { autoTitle: boolean }).autoTitle = true;
    }

    return definitions;
  }

  #isOnDemandExecution () {
    return this.getAttribute(ON_DEMAND_EXECUTION) !== null
      && this.getAttribute(ON_DEMAND_EXECUTION) === 'true';
  }

  #wasExecutionConfirmed () {
    const param = new URLSearchParams(window.location.search);
    const executionConfirmed = param.get(EXECUTION_CONFIRMED);
    return executionConfirmed != null && executionConfirmed === 'true';
  }

  #addDownloadButtonToSubnav () {
    const downloadOptions = this.downloadOptions;
    if (downloadOptions) {
      const subnav = getSubnav();
      if (subnav) {
        this.#excelDownloadButton = createTableExportMenuButton(downloadOptions);
        subnav.addButton(this.#excelDownloadButton);
      }
    }
  }

  get downloadOptions () {
    return parseCommaSeparateAttribute(this.getAttribute('download-options'));
  }

  #onConditionsOrHierarchiesChange (event: CustomEvent<ConditionChangeEventDetails>) {
    this.abort('CONDITION_CHANGE', event.detail.oldConditionValuesSetHash);
    if (event.defaultPrevented) {
      return;
    }
    if (this.shouldUpdateTableConfiguration(event)) {
      this.updateTableConfiguration();
    } else {
      this.refresh();
    }
  }

  /**
   * Returns true if the table needs a complete reinitialization instead of just a data reload.
   *
   * @param conditionsOrHierarchiesChangedEvent - event triggering the reinitialization / the reload
   * @return true if the table needs a complete reinitialization instead of just a data reload
   */
  protected shouldUpdateTableConfiguration (conditionsOrHierarchiesChangedEvent: CustomEvent<ConditionChangeEventDetails>) {
    // Table configuration needs to be updated if we're working with old pivot tables.
    // The only way to check that at this point is to see if at least one column name
    // contains '/'. This should be removed once the support for old pivot tables is obsolete.
    const isOldPivotTable = this.#preparedColumns && this.#preparedColumns.columns.some(column =>
      column.name.includes('/'));
    const detail = conditionsOrHierarchiesChangedEvent.detail;
    return (detail.hierarchyNames && detail.hierarchyNames.length > 0)
      || !this.htmlTable
      || isOldPivotTable;
  }

  #onLoadDataCanceled (detail: BaseTableDataLoadCanceledEventDetails) {
    if (this.getAbortReason() === 'MANUAL') {
      const onDemandView = getOnDemandViewElement();
      if (onDemandView) {
        displayOnDemandViewWithStateCanceled(() => this.refresh(), detail.conditionValueSetHash);
      }
    }
  }

  override onHtmlTableCreated (columns: TableColumnDto[]) {
    this.#addFooter(columns);
  }

  #addFooter (columns: TableColumnDto[]) {
    const tfoot = h('tfoot',
      h('tr', [
        h('td'), // Row number column
        ...columns.map(_ => h('td'))
      ]));
    this.htmlTable!.append(tfoot);
  }

  override hasFooter () {
    return !!this.#aggregationFooters?.length;
  }

  /**
   * Move tfoot under thead and tbody to avoid weird behavior when generating multi-page reports
   * (missing top borders in footer cells).
   */
  #correctFooterPosition () {
    const footer = this.querySelector<HTMLElement>('.display.dataTable tfoot')!;
    footer.parentElement!.append(footer);
  }

  #fillFooter (columns: PreparedOlapPivotTableColumn[]) {
    const anyColumnHaveFooterSubTitle = columns.some(column => column.footerSubTitle);
    const numberColumnFooterSubTitle = anyColumnHaveFooterSubTitle ? i18n('rows') : '';
    this.fillFooterCell(INDEX_COLUMN_INDEX, numberColumnFooterSubTitle, this.numOfRows.toString());  // Row number column
    columns.forEach((c, columnIndex) => {
      this.fillFooterCell(getColumnIndexForFrontend(columnIndex),
        c.footerSubTitle ?? '',
        this.#aggregationFooters![columnIndex]);
    });
    this.resizeTableScrollHeight();
  }

  fillFooterCell (columnIndex: number, title: string, value: string) {
    const dataTableColumn = this.getDataTable()!.column(columnIndex);
    const footer = dataTableColumn.footer() as HTMLTableCellElement;
    if (footer) {
      footer.innerHTML = '';
      footer.append(
        h('.d-table--footerValue', value),
        h('.d-table--footerTitle .ellipsis', title));
      return footer;
    }
  }

  override refreshFooter () {
    this.#fillFooter(this.getColumns());
  }

  /*
   * Prepare columns according to config.
   *
   * @param columnsData - columns configuration
   * @return structure representing the columns and the hierarchicalColumns if any
   */
  protected prepareColumns (columnsData: TableColumnDto[]): PreparedResultTableColumns {
    const rowColumns: PreparedResultTableColumn[] = [];
    columnsData.forEach(column => rowColumns.push(this.prepareColumn(column)));
    const hierarchicalColumns = { rowColumns, multiColumns: [] };
    const columns = [ ...rowColumns ];
    return { columns, hierarchicalColumns };
  }

  /**
   * Prepare column according to config.
   *
   * @param column - column configuration
   * @return structure representing one column
   */
  protected prepareColumn (column: TableColumnDto): PreparedResultTableColumn {
    const preparedColumn = {
      ...COLUMN_DEFAULTS,
      ...column
    };
    if (preparedColumn.processing) {
      preparedColumn.width = '2em';
    }
    return preparedColumn;
  }

  #getProcessingColumnRenderer (column: TableColumnDto): FunctionColumnRender {
    const contextPath = window.Disy.contextPath;
    const linkIcon = (column.processingIcon! in PROCESSING_ICONS)
      ? icon(PROCESSING_ICONS[column.processingIcon!])
      : h('span.d-icon', {
        role: 'presentation',
        style: {
          backgroundImage: `url('${contextPath + encodeUriAndSingleQuote(column.processingIcon!)}')`
        }
      });

    // We have to additionally remove single quotes from the title to prevent malicious values
    // from breaking out of the generated javascript code below
    const escapedTitle = removeSingleQuotes(escapeHTML(column.title));

    return (data, type, row) => {
      const resultTableProcessing = this.#setAdditionallyNeededProcessingFields(
        column.templateProcessing!,
        row.index);

      const processingAsJson = JSON.stringify(resultTableProcessing);
      // "encodeURIComponent" does not encode single quotes, they would break out of the generated
      // javascript below, therefore we remove them as well
      const encodedProcessingAsJson = removeSingleQuotes(encodeURIComponent(processingAsJson));
      const linkHtml = h('a', {
        href: resultTableProcessing.url,
        title: column.title,
        'data-onclick-placeholder': '{{onclick}}'
      }, linkIcon).outerHTML;
      const onclick = `window.disyCommons.executeSingleResultProcessing(event, '${escapedTitle}', '${encodedProcessingAsJson}')`;
      return linkHtml.replace('data-onclick-placeholder="{{onclick}}"', `onclick="${onclick}"`);
    };
  }

  #setAdditionallyNeededProcessingFields (processing: DefaultProcessingDto | ChainingProcessingDto | WebApplicationProcessingDto, rowIndex: number) {
    const resultTableProcessing: ResultTableProcessing = processing as ResultTableProcessing;

    resultTableProcessing.sourceProcessingRow = rowIndex;
    // Do not take the current order from URL parameters, as it was done before,
    // because they are updated too late!
    const order = this.order;
    if (order) {
      resultTableProcessing.sourceOrderByColumn = order.columnIndex;
      resultTableProcessing.sourceOrderAsc = order.order === 'asc';
    }
    resultTableProcessing.url = getProcessingUrl(processing);
    return resultTableProcessing;
  }

  /**
   * Flag that prevents the table from executing the AJAX request
   * It is a workaround for showing a message about invalid table configuration
   *
   * @return Is the current table configuration/table settings valid?
   */
  get isInvalidTableConfiguration () {
    return this.#isInvalidTableConfiguration;
  }

  /**
   * Flag that prevents the table from executing the AJAX request
   * It is a workaround for showing a message about invalid table configuration
   * Shows "invalidTableConfigurationMessage" and hides other messages
   *
   * @param isInvalidTableConfiguration - Is the current table configuration/table settings valid?
   */
  set isInvalidTableConfiguration (isInvalidTableConfiguration: boolean) {
    this.#isInvalidTableConfiguration = isInvalidTableConfiguration;
    this.updateMessages();
  }

  protected updateMessages () {
    this.hideMessages();

    if (this.isInvalidTableConfiguration) {
      this.#invalidTableConfigurationMessage.hidden = false;
    }
  }

  protected hideMessages () {
    this.alerts.alerts = [];
    this.#invalidTableConfigurationMessage.hidden = true;
  }

}

customElements.define(COMPONENT_NAME, ResultTable);

window.disyCommons.executeSingleResultProcessing = function (clickEvent, title, urlEncodedProcessingAsJson) {
  const processingAsJson = decodeURIComponent(urlEncodedProcessingAsJson);
  const processing = JSON.parse(processingAsJson);

  if (doesUserTryToOpenInNewTabOrNewWindow(event)
    && !isCustomTemplatePostMessageProcessing(processing)) {
    return; // nothing to do here, link will open in new tab or new window
  }
  clickEvent.preventDefault();
  showProcessDialog(title, executeParallelViewsProcess(processing));
};

function removeSingleQuotes (input: string) {
  return input.replace(/'/g, '');
}

function addIndexColumn (rows: TableRowDto[], from: number) {
  let rowNumber = from + 1;
  rows.forEach(row => {
    row.values.unshift(rowNumber);
    row.formatted.unshift(rowNumber.toString());
    rowNumber++;
  });
}

function defaultStateSaveCallback (data: DataTableState) {
  const url = new URL(location.href);
  // Find the first item that is sorted and set url parameters from that item.
  // If no sorting is set, go with the default.
  const order = data.order
    .find(([ _, sortDirection ]) => !!sortDirection);
  if (order) {
    // The index column shifts the index here by one, which needs to be
    // subtracted to arrive at the backend's index.
    url.searchParams.set(RESULT_TABLE_OPTION_ORDERBYCOLUMN, getColumnIndexForBackend(order[0]).toString());
    url.searchParams.set(RESULT_TABLE_OPTION_ORDERASC, (order[1] === 'asc').toString());
  } else {
    url.searchParams.delete(RESULT_TABLE_OPTION_ORDERBYCOLUMN);
    url.searchParams.delete(RESULT_TABLE_OPTION_ORDERASC);
  }
  history.replaceState(null, '', url);
}

function defaultStateLoadCallback (): DataTableState | null {
  const param = new URLSearchParams(window.location.search);
  const paramOrderByColumn = param.get(RESULT_TABLE_OPTION_ORDERBYCOLUMN);
  const paramOrderAsc = param.get(RESULT_TABLE_OPTION_ORDERASC);
  if (paramOrderByColumn != null) {
    return {
      time: Date.now(),
      order: [ [
        getColumnIndexForFrontend(Number(paramOrderByColumn)),
        paramOrderAsc !== 'false'
          ? SortOrder.ASC
          : SortOrder.DESC
      ] ]
    };
  }
  return null; // Do not remove this, datatables needs it. Returning undefined does not render the table
}
